import { useState } from "react";
import Modal from "../../../../shared/components/dashboard-layout/modalComponent";
import "./organization-settings-modal.scss";

// Default webhook URLs (if alternative not provided in .env)
const CORAL_WEBHOOK_URL = "https://jely-staging-group-api-backend-5fg3m.ondigitalocean.app/webhook/coral";
const SENDGRID_WEBHOOK_URL = "https://jely-staging-group-api-backend-5fg3m.ondigitalocean.app/webhook/sendgrid";

// Mapping of category => array of [name, label, description, getter]
const SETTINGS_FIELDS = {
  "General Settings": [
    ["platformNumber", "Platform Number", "WhatsApp number associated with Coral. (begin with area code and without spaces)", (orgSettings) => orgSettings.platformNumber],
    ["cswRequestTemplateName", "CSW Request Template Name", "Template name to use when requesting that the customer service window be opened.", (orgSettings) => orgSettings.cswRequestTemplateName],
  ],
  "Alert Settings": [
    ["alertIfMessageIncludes", "Alert If Message Includes", "The phrase that makes any received message an alert - which can be forwarded to an email", (orgSettings) => orgSettings.apiKeys?.alertIfMessageIncludes],
  ],
  "Coral Settings": [
    ["coralApiKey", "Coral API Key", "Coral API key (provided by Coral)", (orgSettings) => orgSettings.apiKeys?.coralApiKey],
    ["coralApiSecret", "Coral API Secret", "Coral API secret (provided by Coral)", (orgSettings) => orgSettings.apiKeys?.coralApiSecret],
  ],
  "Webhook Settings": [
    ["googleChatSpaceWebhookUrl", "Google Chat Space Webhook URL", "A Google Space Webhook URL to send forward message receive notifications to.", (orgSettings) => orgSettings.webhookUrl],
    ["webhookUrl", "Webhook URL", "An optional URL to forward message receive events to.", (orgSettings) => orgSettings.webhookUrl],
  ],
  // "HubSpot Settings": [
  //   ["hubspotId", "HubSpot ID", "HubSpot ID (provided by HubSpot). Used to associate requests sent from a Hubspot plugin.", (orgSettings) => orgSettings.apiKeys?.hubspotId],
  // ],
  "Sendgrid Settings": [
    ["sendgridApiKey", "Sendgrid API Key", "Sendgrid API Key (provided by Sendgrid)", (orgSettings) => orgSettings.apiKeys?.sendgridApiKey],
    ["sendgridFromEmail", "Sendgrid From Email", "Sendgrid sender email to send alerts from (must be a valid email)", (orgSettings) => orgSettings.apiKeys?.sendgridFromEmail],
  ],
  "OpenAI Settings": [
    ["openaiApiKey", "OpenAI API Key", "OpenAI API key to use for summarising alerts. If not provided, no alerts will be summarised.", (orgSettings) => orgSettings.apiKeys?.openaiApiKey],
    ["openaiPromptId", "OpenAI Prompt ID", "The ID of the prompt to use.", (orgSettings) => orgSettings.apiKeys?.openaiPromptId],
    ["openaiPromptVersion", "OpenAI Prompt Version", "The version of the prompt to use.", (orgSettings) => orgSettings.apiKeys?.openaiPromptVersion],
  ],
};

const CATEGORY_DESCRIPTIONS = {
  // "Coral Settings": `Coral API must have its webhook set to ${process.env.REACT_APP_CORAL_SERVICE_DEFAULT_WEBHOOK || CORAL_WEBHOOK_URL}`,
  "Alert Settings": `An alert is a message containing a flagged phrase. If no phrase is provided, alerts cannot be flagged.`,
  "Webhook Settings": `Receive webhook events from WhatsApp groups. For Google Chat Space integration, see https://developers.google.com/workspace/chat/quickstart/webhooks#register-webhook.`,
  // "HubSpot Settings": `HubSpot ID (provided by HubSpot). Used to associate requests sent from a Hubspot plugin.`,
  "Sendgrid Settings": `Alerts can be forwarded to emails using Sendgrid. Sendgrid must have its "Inbound Parse" webhook URL set to ${process.env.REACT_APP_SENDGRID_SERVICE_DEFAULT_WEBHOOK || SENDGRID_WEBHOOK_URL}`,
  "OpenAI Settings": "An OpenAI prompt can be used to summarise alerts when they are received. If not provided, alerts will not be summarised. To create a prompt, visit https://platform.openai.com/playground"
}

const OrganizationSettingsModal = ({ organizationSettings, isOpen, onClose, onUpdateSettings, isLoading }) => {
  // Consolidated state
  const [formData, setFormData] = useState({
    coralApiKey: organizationSettings?.apiKeys?.coralApiKey || "",
    coralApiSecret: organizationSettings?.apiKeys?.coralApiSecret || "",
    alertIfMessageIncludes: organizationSettings?.apiKeys?.alertIfMessageIncludes || "",
    openaiApiKey: organizationSettings?.apiKeys?.openaiApiKey || "",
    openaiPromptId: organizationSettings?.apiKeys?.openaiPromptId || "",
    openaiPromptVersion: organizationSettings?.apiKeys?.openaiPromptVersion || "",
    sendgridApiKey: organizationSettings?.apiKeys?.sendgridApiKey || "",
    sendgridFromEmail: organizationSettings?.apiKeys?.sendgridFromEmail || "",
    webhookUrl: organizationSettings?.webhookUrl || "",
    googleChatSpaceWebhookUrl: organizationSettings?.googleChatSpaceWebhookUrl || "",
    platformNumber: organizationSettings?.platformNumber || "",
    hubspotId: organizationSettings?.hubspotId || "",
    cswRequestTemplateName: organizationSettings?.cswRequestTemplateName || "",
  });
  const [errors, setErrors] = useState({});
  const [showField, setShowField] = useState({});

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Form validation utility
  const validateForm = () => {
    const newErrors = {};

    // Check that Coral API key and secret, and phone number are both provided
    if (!formData.coralApiKey.trim()) {
      newErrors.coralApiKey = "Coral API key is required";
    }
    if (!formData.coralApiSecret.trim()) {
      newErrors.coralApiSecret = "Coral API secret is required";
    }
    if (!formData.platformNumber.trim()) {
      newErrors.platformNumber = "Platform number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Event handlers
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onUpdateSettings({ 
        apiKeys: {
          coralApiKey: formData.coralApiKey.trim(),
          coralApiSecret: formData.coralApiSecret.trim(),
          alertIfMessageIncludes: formData.alertIfMessageIncludes.trim(),
          openaiApiKey: formData.openaiApiKey.trim(),
          openaiPromptId: formData.openaiPromptId.trim(),
          openaiPromptVersion: formData.openaiPromptVersion.trim(),
          sendgridApiKey: formData.sendgridApiKey.trim(),
          sendgridFromEmail: formData.sendgridFromEmail.trim(),
        },
        webhookUrl: formData.webhookUrl.trim(),
        googleChatSpaceWebhookUrl: formData.googleChatSpaceWebhookUrl.trim(),
        platformNumber: formData.platformNumber.trim(),
        hubspotId: formData.hubspotId.trim(),
        cswRequestTemplateName: formData.cswRequestTemplateName.trim(),
      });
    }
  };

  const handleSave = (e) => {
    if (isLoading) return;
    handleSubmit(e);
  };

  const renderField = (field, label, description) => {
    const isShown = showField[field] !== undefined ? showField[field] : false; // default to hidden (password)
    const inputType = isShown ? "text" : "password";
    return (
      <div className="form-group">
        <label htmlFor={field}>{description}</label>
        <div className="d-flex align-items-stretch" style={{ gap: 0 }}>
          <input
            type={inputType}
            id={field}
            value={formData[field]}
            onChange={(e) => updateFormField(field, e.target.value)}
            placeholder={`Enter ${label}`}
            className={errors[field] ? "error form-control" : "form-control"}
            autoComplete="off"
            style={{ borderRight: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          />
          <button
            type="button"
            className="btn btn-outline-secondary border-start-0"
            onClick={() => setShowField(prev => ({ ...prev, [field]: !isShown }))}
            style={{ borderLeft: 'none', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderColor: '#dee2e6', borderWidth: '2px' }}
            aria-label={isShown ? `Hide ${label}` : `Show ${label}`}
            title={isShown ? `Hide ${label}` : `Show ${label}`}
          >
            {isShown ? '🙈' : '👁️'}
          </button>
        </div>
        {errors[field] && (
          <span className="error-message">{errors[field]}</span>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      header={"Edit Organization"}
      buttons={true}
      onSave={handleSave}
      saveDisabled={isLoading}
    >
      <div className="organization-settings-modal">
        <form onSubmit={handleSubmit} className="modal-form" style={{ padding: "0" }}>
          <i style={{ fontSize: "12px" }}>Changes may take a few minutes to take effect.</i>
          <div style={{ paddingBottom: "16px" }}></div>

          {Object.entries(SETTINGS_FIELDS).map(([category, fields]) => (
            <div key={category} className="settings-category">
              <h3>{category}</h3>
              {CATEGORY_DESCRIPTIONS[category] && <span>{CATEGORY_DESCRIPTIONS[category]}</span>}
              {fields.map(([name, label, description]) => (
                <>{renderField(name, label, description)}</>
              ))}
            </div>
          ))}
        </form>
      </div>
    </Modal>
  );
};

export default OrganizationSettingsModal;
